import { combine, createEvent, createStore, restore, sample } from 'effector'
import { createGate } from 'effector-react'
import {
  NotificationPayload,
  showNotification,
} from 'src/features/notifications'
import { getBudgetErrorNotification } from 'src/pages/budget/errorNotification'
import { fx, role } from 'src/libs'
import {
  User,
  Team,
  UsersResponse,
  AddUserPayload,
  DeleteUserPayload,
  AddUserResponse,
  DeleteUserResponse,
  PermissionType,
  DeleteUserPayloadModal,
  DeleteUserModalStore,
} from 'src/pages/budget/budgetTeams/types'
import {
  addUsersToTeamList,
  formatTeamsToTree,
} from 'src/pages/budget/budgetTeams/utils'
import { BUDGET_STEPS } from 'src/pages/budget/const'

import { $users, getTeamListFx, getUsersFx } from 'src/pages/budget/model'

export const TeamListGate = createGate()
export const UserListGate = createGate()
export const ArticlesByUserGate = createGate<{ userId: string | undefined }>()

export const submitAddUser = createEvent<AddUserPayload>()
export const submitDeleteUser = createEvent<DeleteUserPayload>()

export const openModalAddUser = createEvent<AddUserPayload>()
export const closeModalAddUser = createEvent()
export const openModalDeleteUser = createEvent<DeleteUserPayloadModal>()
export const closeModalDeleteUser = createEvent()

export const getUserListFx = fx<void, UsersResponse>({
  method: 'GET',
  url: '/budget/users',
})
export const submitAddUserFx = fx<AddUserPayload, AddUserResponse>({
  method: 'POST',
  url: '/budget/budget_permission/add',
})
export const submitDeleteUserFx = fx<DeleteUserPayload, DeleteUserResponse>({
  method: 'POST',
  url: '/budget/budget_permission/delete',
})
export const getArticlesByUserIdFx = fx<
  { userId: string },
  { items: { id: number; name: string }[] }
>({
  method: 'GET',
})

export const $teamList = createStore<Team[]>([]).on(
  getTeamListFx.doneData,
  (_, res) => res.body.items,
)

export const $teamsTree = combine($teamList, (teamList) =>
  formatTeamsToTree(teamList),
)

export const $teamMap = $teamList.map((teams) => {
  const getPermissions = (team: Team, type: PermissionType): string[] => {
    const parent = teams.find((item) => item.id === team.parentTeamId)

    return [
      ...(team.permissions[type] ?? []),
      ...(parent ? getPermissions(parent, type) : []),
    ]
  }

  return new Map(
    teams.map((team) => [
      team.id,
      {
        name: team.name,
        permissions: {
          submit: getPermissions(team, 'submit'),
          approve: getPermissions(team, 'approve'),
          pay: getPermissions(team, 'pay'),
        },
      },
    ]),
  )
})

export const $userArticleList = createStore<{ id: number; name: string }[]>([])
  .on(getArticlesByUserIdFx.doneData, (_, res) => res.body.items)
  .reset(ArticlesByUserGate.close)


export const $userList = restore<User[]>(
  getUserListFx.doneData.map((a) => a.body.items),
  [],
)

export const $forbiddenDeleteUsersId = restore<Set<string>>(
  getUsersFx.doneData.map(({ body }) => {
    return new Set(
      body.items
        .filter((user) => user.roles.includes(role.head.ceo()))
        .map((user) => user.id),
    )
  }),
  new Set<string>(),
)

export const $modalAddUsers = createStore<AddUserPayload | null>(null)
  .on(openModalAddUser, (_, payload) => payload)
  .reset(closeModalAddUser)

export const $modalDeleteUser = createStore<DeleteUserModalStore>({
  data: null,
  isOpenDeleteModal: false,
})
  .on(openModalDeleteUser, (_, payload) => ({
    data: { ...payload },
    isOpenDeleteModal: true,
  }))
  .reset(closeModalDeleteUser)

sample({
  clock: [TeamListGate.open, submitDeleteUserFx.done],
  fn: () => ({}),
  target: [getTeamListFx, getUsersFx],
})

sample({
  clock: [UserListGate.open, submitDeleteUserFx.done, submitAddUserFx.done],
  fn: () => ({}),
  target: getUserListFx,
})

sample({
  clock: ArticlesByUserGate.open,
  fn: ({ userId }) => {
    return {
      url: `/budget/users/${userId}/articles`,
    }
  },
  target: getArticlesByUserIdFx,
})

sample({
  clock: submitAddUser,
  fn: (params) => ({
    body: { ...params },
  }),
  target: submitAddUserFx,
})

sample({
  clock: submitDeleteUser,
  fn: (params) => ({
    body: { ...params },
  }),
  target: submitDeleteUserFx,
})

sample({
  clock: submitAddUserFx.doneData,
  source: $teamList,
  fn: (teams, response) => {
    const addedUsers = response?.body ?? []
    return addUsersToTeamList(teams, addedUsers)
  },
  target: $teamList,
})

sample({
  clock: submitAddUserFx.done,
  source: {
    users: $users,
    teamMap: $teamMap,
  },
  fn: ({ users, teamMap }, { params }): NotificationPayload => {
    const body = params?.body

    if (!body) {
      return {
        type: 'success',
        description: 'Пользователь успешно добавлен в команду',
      }
    }
    const { allUsersMap } = users
    const { userIds, teamId, type } = body
    const isSingle = userIds.length === 1
    const userNames = userIds
      .map((id: string) => allUsersMap.get(id))
      .join(', ')

    const teamName = teamId ? teamMap.get(teamId)?.name ?? '' : ''

    const subject = isSingle ? 'Пользователь' : 'Пользователи'
    const verb = isSingle ? 'добавлен' : 'добавлены'

    const description = `${subject} ${userNames} успешно ${verb} в команду ${teamName} - ${BUDGET_STEPS[type]}`

    return {
      type: 'success',
      description,
    }
  },
  target: showNotification,
})

sample({
  clock: submitDeleteUserFx.done,
  source: {
    users: $users,
    teamMap: $teamMap,
  },
  fn: ({ users, teamMap }, { params }): NotificationPayload => {
    const body: DeleteUserPayload | undefined = params?.body
    if (!body) {
      return {
        type: 'success',
        description: 'Пользователь успешно удалён из команды',
      }
    }
    const { allUsersMap } = users
    const { userId, teamId, type } = body

    const userName: string = allUsersMap.get(userId) ?? ''
    const teamName = teamId ? teamMap.get(teamId)?.name : ''

    return {
      type: 'success',
      description: `Пользователь ${userName} успешно удалён из команды ${teamName} - ${BUDGET_STEPS[type]}`,
    }
  },
  target: showNotification,
})

sample({
  clock: [submitAddUserFx.failData, submitDeleteUserFx.failData],
  fn: getBudgetErrorNotification,
  target: showNotification,
})
